import {isFeatureEnabled} from '@github-ui/feature-flags'
import {DEFAULT_MAXIMUM_ASSIGNEES} from '@github-ui/item-picker-assignee'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {testIdProps} from '@github-ui/test-id-props'
import type {OverlayProps} from '@primer/react'
import {SelectPanel} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect} from 'react'

import {SystemColumnId} from '../../../api/columns/contracts/memex-column'
import type {SupportedSuggestionOptions} from '../../../helpers/suggestions'
import {useAssigneeLimitReached} from '../../../hooks/editors/use-assignee-limit'
import {useSelectPanel, type UseSelectPanelProps} from '../../../hooks/editors/use-select-panel'
import type {ColumnModel} from '../../../models/column-model'
import type {MemexItemModel, RedactedItemModel} from '../../../models/memex-item-model'
import {FocusType} from '../../../navigation/types'
import {RepoPicker} from '../../repo-picker'
import {moveTableFocus, useStableTableNavigation, useTableNavigationFocusInitialValue} from '../navigation'
import {cellEditorTestId} from '../test-identifiers'
import styles from './select-panel-editor.module.css'
import {useConvertToIssueHelper} from './use-convert-to-issue-helper'
import {useSummaryAnchor} from './use-summary-anchor'

type InteractableMemexItemModel = Exclude<MemexItemModel, RedactedItemModel>

const overlayProps: Partial<OverlayProps> = {
  width: 'small',
  onMouseDown: e => {
    // prevent click from bubbling out of the overlay, which would change focus state in the table cell
    e.stopPropagation()
  },
}

export type TSelectPanelEditorProps = Readonly<{
  model: InteractableMemexItemModel
  rowIndex: number
  columnId: ColumnModel['id']
}>

type SelectPanelEditorProps<T extends SupportedSuggestionOptions> = Pick<
  OverlayProps,
  'height' | 'maxHeight' | 'anchorSide'
> &
  Omit<UseSelectPanelProps<T>, 'initialFilterValue' | 'onOpenChange' | 'renderAnchor'> & {
    model: InteractableMemexItemModel
    columnId: ColumnModel['id']
    rowIndex: number
    renderButton: () => React.ReactNode
    isIssueField?: boolean
    maximumAssignees?: number
  }

export function SelectPanelEditor<T extends SupportedSuggestionOptions>({
  model,
  columnId,
  rowIndex,
  renderButton,
  height,
  maxHeight,
  anchorSide,
  isIssueField,
  maximumAssignees = DEFAULT_MAXIMUM_ASSIGNEES,
  ...useSelectPanelProps
}: Omit<SelectPanelEditorProps<T>, 'open'>) {
  const {navigationDispatch} = useStableTableNavigation()
  const initialFilterValue = useTableNavigationFocusInitialValue()
  const {renderAnchor, onClickOutside, onOpenChange} = useSummaryAnchor({columnId, renderButton})
  const {showConvertToIssue, onRowConvertSuccess} = useConvertToIssueHelper({model, columnId, isIssueField})

  useEffect(() => {
    navigationDispatch(moveTableFocus({focusType: FocusType.Suspended}))
  }, [model.contentType, navigationDispatch])

  const selectPanelProps = useSelectPanel({
    ...useSelectPanelProps,
    model,
    initialFilterValue,
    onOpenChange,
    columnId,
    renderAnchor,
    maximumAssignees,
  })

  const selectedCount = selectPanelProps.selected?.length ?? 0
  const isAssigneeLimitReached = useAssigneeLimitReached(columnId, selectedCount, maximumAssignees)
  const isFeatureFlagEnabled = isFeatureEnabled('projects_assignee_max_limit')

  /**
   * For Draft Issues, we show this repository picker to allow the user to choose a
   * repository and convert to an Issue, in order to edit it. This is only shown for
   * retricted columns.
   * */
  if (showConvertToIssue) {
    return (
      <RepoPicker
        renderAnchor={renderAnchor}
        isOpen
        showPrompt={columnId !== SystemColumnId.Repository}
        item={model}
        onOpenChange={onOpenChange}
        onSuccess={onRowConvertSuccess}
      />
    )
  }

  return (
    <SelectPanel
      {...selectPanelProps}
      notice={
        isFeatureFlagEnabled && isAssigneeLimitReached
          ? {
              text: LABELS.limitWarning(maximumAssignees),
              variant: 'warning',
            }
          : undefined
      }
      overlayProps={{
        ...overlayProps,
        height,
        maxHeight,
        anchorSide,
        className: clsx(styles.Overlay, overlayProps.className),
        onClickOutside,
        ...testIdProps(cellEditorTestId(rowIndex, columnId.toString())),
      }}
    />
  )
}

SelectPanelEditor.displayName = 'SelectPanelEditor'
